import {
  createContext,
  useState,
  useRef,
  useContext,
  useCallback,
  useMemo,
  Fragment,
} from 'react'
import RealtimeAttendance from '@/modules/RealtimeAttendance/classes/RealtimeAttendance'
import { useForceUpdate } from '@react-spring/shared'
import { FunctionComponentChildren } from '@/@types/react'

type updateRAFnType = (RA: RealtimeAttendance) => RealtimeAttendance
export interface IRealtimeAttendanceContext {
  realtimeAttendance: RealtimeAttendance
  updateRealtimeAttendance(call: updateRAFnType): void
  clearRealtimeAttendance(): void
  callingTimes: number
  startTimer: () => void
  clearTimer: () => void
}

const RealtimeAttendanceContext =
  createContext<IRealtimeAttendanceContext>(null)

export const RealtimeAttendanceProvider: FunctionComponentChildren = ({
  children,
}) => {
  const [realtimeAttendance, setRealtimeAttendance] =
    useState<RealtimeAttendance>(() => new RealtimeAttendance())
  const [callingTimes, setCallingTimes] = useState<number>(0)
  const forceUpdate = useForceUpdate()
  const callingTimesInterval = useRef<NodeJS.Timeout>(null)

  const updateRealtimeAttendance = useCallback(
    (call: updateRAFnType) => {
      const newRA = call(realtimeAttendance)
      setRealtimeAttendance(newRA)
      forceUpdate()
    },
    [forceUpdate, realtimeAttendance],
  )
  const clearRealtimeAttendance = useCallback(() => {
    setRealtimeAttendance(realtimeAttendance => {
      return realtimeAttendance.clear()
    })
    forceUpdate()
  }, [forceUpdate])

  const startTimer = useCallback(() => {
    callingTimesInterval.current = setInterval(() => {
      setCallingTimes(prev => prev + 1)
    }, 4000)
  }, [])

  const clearTimer = useCallback(() => {
    clearTimeout(callingTimesInterval.current)
    setCallingTimes(0)
  }, [])

  const realTimeAttendanceValue = useMemo(() => {
    return {
      realtimeAttendance,
      updateRealtimeAttendance,
      clearRealtimeAttendance,
      callingTimes,
      startTimer,
      clearTimer,
    }
  }, [
    callingTimes,
    clearRealtimeAttendance,
    clearTimer,
    realtimeAttendance,
    startTimer,
    updateRealtimeAttendance,
  ])

  return (
    <Fragment>
      <RealtimeAttendanceContext.Provider value={realTimeAttendanceValue}>
        {children}
      </RealtimeAttendanceContext.Provider>
    </Fragment>
  )
}

export const useRealtimeAttendanceContext = (): IRealtimeAttendanceContext => {
  const context = useContext(RealtimeAttendanceContext)
  if (!context) {
    throw new Error(
      'useRealtimeAttendanceContext must be used within a RealtimeAttendanceProvider',
    )
  }
  return context
}
